import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, LogOut, RefreshCw, Inbox, Lock, Download } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;
const TOKEN_KEY = "ng_admin_token";

const inputCls =
  "w-full border border-forest-border bg-forest-surface px-4 py-3.5 text-sm font-light text-cream placeholder:text-sage/50 outline-none transition-colors duration-300 focus:border-gold";

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [signingIn, setSigningIn] = useState(false);
  const [enquiries, setEnquiries] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (t) => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/enquiries`, {
        headers: { Authorization: `Bearer ${t}` },
      });
      setEnquiries(data.enquiries);
    } catch (e) {
      if (e.response?.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        setToken("");
        setEnquiries(null);
      } else {
        toast.error("Could not load enquiries.");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (token) load(token);
  }, [token, load]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSigningIn(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : "Sign in failed. Please try again.");
    } finally {
      setSigningIn(false);
    }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setEnquiries(null);
  };

  const exportCsv = () => {
    if (!enquiries?.length) {
      toast.error("No enquiries to export yet.");
      return;
    }
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const headers = ["Date", "Name", "Email", "Phone", "Company", "Country", "Product Interest", "Message"];
    const rows = enquiries.map((q) =>
      [
        new Date(q.created_at).toLocaleString("en-IN"),
        q.name, q.email, q.phone, q.company, q.country, q.product_interest, q.message,
      ].map(esc).join(",")
    );
    const csv = "\uFEFF" + headers.map(esc).join(",") + "\n" + rows.join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `nishmayy-enquiries-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${enquiries.length} enquiries to CSV.`);
  };

  if (!token) {
    return (
      <main className="mx-auto flex max-w-md flex-col px-6 py-32" data-testid="admin-login">
        <span className="flex h-12 w-12 items-center justify-center border border-gold/40 text-gold">
          <Lock className="h-5 w-5" strokeWidth={1.5} />
        </span>
        <h1 className="mt-6 font-serif text-4xl font-light text-cream">
          Enquiry Inbox<span className="text-gold">.</span>
        </h1>
        <p className="mt-3 text-sm font-light text-sage">Private area. Sign in with your admin account.</p>
        <form onSubmit={submit} className="mt-10 space-y-5" data-testid="admin-login-form">
          <div>
            <label htmlFor="admin-email" className="mb-2 block text-xs uppercase tracking-[0.2em] text-sage">Email</label>
            <input id="admin-email" data-testid="admin-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="admin@nishmayy.com" className={inputCls} />
          </div>
          <div>
            <label htmlFor="admin-password" className="mb-2 block text-xs uppercase tracking-[0.2em] text-sage">Password</label>
            <input id="admin-password" data-testid="admin-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className={inputCls} />
          </div>
          {error && <p className="text-sm text-red-400" data-testid="admin-login-error">{error}</p>}
          <button
            type="submit"
            disabled={signingIn}
            data-testid="admin-login-button"
            className="flex w-full items-center justify-center gap-3 bg-gold px-8 py-4 text-sm font-semibold tracking-wide text-forest transition-colors duration-300 hover:bg-cream disabled:opacity-60"
          >
            {signingIn ? <><Loader2 className="h-4 w-4 animate-spin" /> Signing in…</> : "Sign In"}
          </button>
        </form>
      </main>
    );
  }

  return (
    <main className="mx-auto max-w-7xl px-6 py-20 lg:px-10" data-testid="admin-inbox">
      <div className="flex flex-wrap items-center justify-between gap-6">
        <div>
          <h1 className="font-serif text-4xl font-light text-cream md:text-5xl">
            Enquiry Inbox<span className="text-gold">.</span>
          </h1>
          <p className="mt-2 text-sm font-light text-sage" data-testid="admin-count">
            {enquiries ? `${enquiries.length} ${enquiries.length === 1 ? "enquiry" : "enquiries"} received` : "Loading…"}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={exportCsv}
            data-testid="admin-export-button"
            className="flex items-center gap-2 border border-gold bg-gold px-5 py-3 text-sm font-semibold text-forest transition-colors duration-300 hover:bg-cream"
          >
            <Download className="h-4 w-4" /> Export CSV
          </button>
          <button
            onClick={() => load(token)}
            data-testid="admin-refresh-button"
            className="flex items-center gap-2 border border-forest-border px-5 py-3 text-sm text-sage transition-colors duration-300 hover:border-gold hover:text-gold"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
          <button
            onClick={logout}
            data-testid="admin-logout-button"
            className="flex items-center gap-2 border border-forest-border px-5 py-3 text-sm text-sage transition-colors duration-300 hover:border-gold hover:text-gold"
          >
            <LogOut className="h-4 w-4" /> Sign Out
          </button>
        </div>
      </div>

      <div className="mt-12 space-y-4">
        {enquiries && enquiries.length === 0 && (
          <div className="flex flex-col items-center border border-forest-border bg-forest-surface px-6 py-20 text-center" data-testid="admin-empty">
            <Inbox className="h-10 w-10 text-gold/50" strokeWidth={1.25} />
            <p className="mt-4 font-serif text-2xl font-light text-cream">No enquiries yet</p>
            <p className="mt-2 text-sm font-light text-sage">New website enquiries will appear here.</p>
          </div>
        )}
        {enquiries?.map((q) => (
          <article key={q.id} className="border border-forest-border bg-forest-surface p-6 lg:p-8" data-testid={`enquiry-${q.id}`}>
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <h3 className="font-serif text-2xl font-light text-cream">{q.name}</h3>
                <p className="mt-1 text-sm text-sage">
                  {[q.company, q.country].filter(Boolean).join(" · ")}
                </p>
              </div>
              <div className="text-right">
                {q.product_interest && (
                  <span className="border border-gold/40 px-3 py-1 text-[11px] uppercase tracking-[0.15em] text-gold">
                    {q.product_interest}
                  </span>
                )}
                <p className="mt-2 text-xs text-sage/70">
                  {new Date(q.created_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}
                </p>
              </div>
            </div>
            <div className="mt-4 flex flex-wrap gap-x-8 gap-y-2 text-sm text-sage">
              <a href={`mailto:${q.email}`} className="transition-colors hover:text-gold" data-testid={`enquiry-email-${q.id}`}>{q.email}</a>
              {q.phone && <span>{q.phone}</span>}
            </div>
            <p className="mt-4 border-t border-forest-border pt-4 text-sm font-light leading-relaxed text-cream/90">
              {q.message}
            </p>
          </article>
        ))}
      </div>
    </main>
  );
}
